/**
 * Elli panel. Talks only to the app-proxy endpoint (same origin); renders
 * strictly via DOM APIs so response data is never parsed as HTML. If the
 * backend is down the shop keeps working — Elli just apologises.
 *
 * Funnel instrumentation: every Elli-originated cart line carries the hidden
 * `_elli` line-item property and the cart gains an `elli_assisted` attribute,
 * which Shopify copies onto the order — the basis for comparing
 * Elli-assisted AOV/conversion with normal shopping.
 */
(() => {
  'use strict';
  const root = document.getElementById('elli');
  if (!root) return;

  const endpoint = root.dataset.endpoint || '/apps/elli/chat';
  const launcher = root.querySelector('[data-elli-launcher]');
  const dialog = root.querySelector('dialog');
  const messagesEl = root.querySelector('[data-elli-messages]');
  const form = root.querySelector('[data-elli-form]');
  const input = root.querySelector('[data-elli-input]');
  const quickActions = root.querySelectorAll('[data-elli-quick]');
  if (!launcher || !dialog || !messagesEl || !form || !input) return;

  const track = (name, data) => window.MEA?.track(name, data);
  const money = (cents, currency) =>
    new Intl.NumberFormat('en-AU', { style: 'currency', currency: currency || 'AUD' }).format(cents / 100);

  /* ---------- trusted product references ----------
     Product URLs are derived HERE from the server-validated handle — never
     taken from any URL field, so a crafted/model-originated URL can never
     become a link. Cart ids are the numeric tail of the validated variant
     gid; anything that doesn't match the strict shape simply cannot be
     linked or carted. */
  const HANDLE_RE = /^[a-z0-9][a-z0-9_-]{0,254}$/;
  const productHref = (handle) =>
    typeof handle === 'string' && HANDLE_RE.test(handle) ? `/products/${handle}` : null;
  const VARIANT_GID_RE = /^gid:\/\/shopify\/ProductVariant\/(\d+)$/;
  const cartId = (gid) => {
    const match = VARIANT_GID_RE.exec(String(gid || ''));
    return match ? Number(match[1]) : null;
  };
  const safeImage = (url) => (typeof url === 'string' && url.startsWith('https://') ? url : null);

  /* ---------- session state (this tab only; no server persistence) ---------- */
  const sessionId = (() => {
    let id = sessionStorage.getItem('elli_session_id');
    if (!id) {
      id = `sess_${crypto.randomUUID().replace(/-/g, '')}`;
      sessionStorage.setItem('elli_session_id', id);
    }
    return id;
  })();

  let history = [];
  try {
    history = JSON.parse(sessionStorage.getItem('elli_history') || '[]');
  } catch {
    history = [];
  }
  const saveHistory = () => {
    // The backend accepts at most 12 bounded messages — mirror that here.
    history = history.slice(-12);
    sessionStorage.setItem('elli_history', JSON.stringify(history));
  };

  /* ---------- conversation state ----------
     Onboarding (greeting + quick actions) exists only before the first turn.
     Sending a message, tapping a quick action, or restoring a session
     conversation flips the panel to the active state and the conversation
     inherits the space. */
  const setStarted = () => {
    dialog.setAttribute('data-started', '');
  };
  if (history.length) setStarted();

  /* ---------- rendering (DOM APIs only) ---------- */
  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  };

  const scrollDown = () => {
    messagesEl.scrollTop = messagesEl.scrollHeight;
  };

  function renderBubble(role, text) {
    const bubble = el('div', `elli-msg elli-msg--${role}`, text);
    messagesEl.append(bubble);
    scrollDown();
    return bubble;
  }

  /* ---------- product cards (grounded, navigable) ----------
     Each recommendation renders as a compact commerce card whose links are
     derived from the trusted handle. A plan item tracks the customer's
     variant choice: multi-variant pieces are never resolved silently — the
     chips below the card must be tapped first. */
  function buildPlanItems(products) {
    return products
      .filter((p) => p && typeof p === 'object')
      .map((product) => {
        const needsChoice =
          product.requires_choice === true &&
          Array.isArray(product.variants) &&
          product.variants.length > 1;
        return {
          product,
          quantity: Math.min(Math.max(Number(product.quantity) || 1, 1), 10),
          needsChoice,
          chosenGid: needsChoice ? null : product.variant_id,
          chosenPrice: product.price,
          chosenTitle: needsChoice ? null : null,
        };
      });
  }

  function renderProducts(planItems, { clickEvent = 'elli_product_click' } = {}) {
    if (!planItems.length) return;
    const wrap = el('div', 'elli-products');
    for (const item of planItems) {
      const { product } = item;
      const href = productHref(product.handle);
      const card = el('article', 'elli-product');

      const main = el(href ? 'a' : 'div', 'elli-product__main');
      if (href) {
        main.href = href;
        main.addEventListener('click', () => track(clickEvent, { handle: product.handle }));
      }
      const image = safeImage(product.image);
      if (image) {
        const img = document.createElement('img');
        img.src = image;
        img.alt = '';
        img.loading = 'lazy';
        main.append(img);
      }
      const info = el('div', 'elli-product__info');
      const priceEl = el(
        'span',
        'elli-product__price',
        money(item.chosenPrice, product.currency) + (item.quantity > 1 ? ` × ${item.quantity}` : ''),
      );
      info.append(el('strong', 'elli-product__title', product.title), priceEl);
      main.append(info);
      card.append(main);
      if (product.reason) card.append(el('p', 'elli-product__reason', product.reason));

      if (item.needsChoice) {
        card.append(el('p', 'elli-product__choose', root.dataset.chooseLabel || 'Which one would you like?'));
        const chips = el('div', 'elli-variants');
        for (const variant of product.variants.slice(0, 10)) {
          if (!cartId(variant.id)) continue; // untrusted shape → not offerable
          const chip = el('button', 'elli-variant', variant.title + (variant.available ? '' : ' — sold out'));
          chip.type = 'button';
          chip.setAttribute('aria-pressed', 'false');
          if (!variant.available) chip.disabled = true;
          chip.addEventListener('click', () => {
            item.chosenGid = variant.id;
            item.chosenPrice = Number.isFinite(variant.price) ? variant.price : product.price;
            item.chosenTitle = variant.title;
            // A variant chosen on a card also satisfies visualisation
            // readiness for the same piece.
            visChosen[product.handle] = variant.id;
            saveVisState();
            chips.querySelectorAll('.elli-variant').forEach((c) => c.setAttribute('aria-pressed', 'false'));
            chip.setAttribute('aria-pressed', 'true');
            priceEl.textContent =
              money(item.chosenPrice, product.currency) + (item.quantity > 1 ? ` × ${item.quantity}` : '');
            refreshPlanBar();
          });
          chips.append(chip);
        }
        card.append(chips);
      }

      if (href) {
        const view = el('a', 'elli-product__view', `${root.dataset.viewLabel || 'View piece'} →`);
        view.href = href;
        view.addEventListener('click', () => track(clickEvent, { handle: product.handle }));
        card.append(view);
      }
      wrap.append(card);
    }
    messagesEl.append(wrap);
    scrollDown();
  }

  /* ---------- plan summary + explicit cart consent ----------
     Elli OFFERS; only an explicit tap on the labelled add button mutates the
     cart. "Not yet" declines. Nothing here runs on model output alone. */
  let activePlanBar = null;

  function refreshPlanBar() {
    activePlanBar?.refresh();
  }

  function renderPlanBar(planItems) {
    const addable = planItems.filter((item) => item.needsChoice || cartId(item.chosenGid));
    if (!addable.length) return;

    // A newer plan supersedes any still-pending offer.
    messagesEl.querySelector('[data-elli-plan-bar]')?.remove();

    const bar = el('div', 'elli-plan');
    bar.setAttribute('data-elli-plan-bar', '');
    bar.append(el('p', 'elli-plan__heading', root.dataset.planHeading || 'Your plan'));
    const totalRow = el('div', 'elli-plan__row');
    const totalValue = el('strong');
    totalRow.append(el('span', null, root.dataset.totalLabel || 'Total'), totalValue);
    bar.append(totalRow);
    bar.append(
      el(
        'p',
        'elli-plan__question',
        addable.length === 1
          ? root.dataset.consentQuestionSingle || 'Would you like me to add it to your cart?'
          : root.dataset.consentQuestion || 'Would you like me to add these to your cart?',
      ),
    );
    const actions = el('div', 'elli-plan__actions');
    const addBtn = el('button', 'btn');
    addBtn.type = 'button';
    const declineBtn = el('button', 'btn elli-plan__decline', root.dataset.notYetLabel || 'Not yet');
    declineBtn.type = 'button';
    actions.append(addBtn, declineBtn);
    bar.append(actions);

    const currency = addable[0].product.currency;
    const resolved = () => addable.filter((item) => cartId(item.chosenGid));
    const total = () => resolved().reduce((sum, item) => sum + item.chosenPrice * item.quantity, 0);

    const refresh = () => {
      const ready = resolved().length === addable.length;
      totalValue.textContent = money(total(), currency);
      if (!ready) {
        addBtn.textContent = root.dataset.chooseFirstLabel || 'Choose options above';
        addBtn.disabled = true;
        return;
      }
      const label =
        addable.length === 1
          ? root.dataset.addOneLabel || 'Add'
          : addable.length === 2
            ? root.dataset.addBothLabel || 'Add both'
            : `${root.dataset.addAllLabel || 'Add all'} ${addable.length}`;
      addBtn.textContent = `${label} — ${money(total(), currency)}`;
      addBtn.disabled = false;
    };

    addBtn.addEventListener('click', () => addPlanToCart(addable, { bar, addBtn, declineBtn, currency }));
    declineBtn.addEventListener('click', () => {
      track('elli_plan_declined', { count: addable.length });
      activePlanBar = null;
      bar.remove();
    });

    activePlanBar = { refresh };
    refresh();
    messagesEl.append(bar);
    track('elli_plan_offer', { count: addable.length });
    scrollDown();
  }

  /* ---------- cart mutation (only ever after the explicit tap above) ---------- */
  async function addPlanToCart(items, ui) {
    const lines = items.map((item) => ({
      id: cartId(item.chosenGid),
      quantity: item.quantity,
      properties: { _elli: '1' },
    }));
    if (lines.some((line) => !line.id)) return; // unresolved/untrusted → never mutate
    ui.addBtn.disabled = true;
    ui.declineBtn.disabled = true;
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: lines }),
      });
      if (!res.ok) throw new Error(String(res.status));

      // First Elli-assisted add of the session marks the whole cart, and the
      // attribute rides through checkout onto the order.
      if (!sessionStorage.getItem('elli_cart_marked')) {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { elli_assisted: 'true' } }),
        });
        sessionStorage.setItem('elli_cart_marked', '1');
      }

      track('elli_add_to_cart', { items: lines.length });
      track('add_to_cart', { source: 'elli', items: lines.length });
      renderCartSuccess(items, ui);
      window.MECart?.refresh(false); // sync the header badge; the panel stays up
    } catch {
      // Shopify did not confirm the mutation — never claim success.
      ui.addBtn.disabled = false;
      ui.declineBtn.disabled = false;
      renderBubble(
        'assistant',
        root.dataset.cartErrorLabel ||
          "I couldn't add those to your cart just now — please try again, or open any piece directly.",
      );
    }
  }

  function renderCartSuccess(items, ui) {
    const block = el('div', 'elli-success');
    block.append(el('p', 'elli-success__heading', root.dataset.addedHeading || 'Added to your garden.'));
    const lines = el('ul', 'elli-success__lines');
    for (const item of items) {
      const name = item.chosenTitle ? `${item.product.title} — ${item.chosenTitle}` : item.product.title;
      lines.append(el('li', null, item.quantity > 1 ? `${name} × ${item.quantity}` : name));
    }
    block.append(lines);
    const total = items.reduce((sum, item) => sum + item.chosenPrice * item.quantity, 0);
    block.append(el('p', 'elli-success__total', money(total, ui.currency)));
    const actions = el('div', 'elli-plan__actions');
    const viewCart = el('button', 'btn', root.dataset.viewCartLabel || 'View cart');
    viewCart.type = 'button';
    viewCart.addEventListener('click', () => {
      dialog.close();
      if (window.MECart) window.MECart.refresh(true);
      else window.location.href = '/cart';
    });
    const keepExploring = el('button', 'btn elli-plan__decline', root.dataset.keepExploringLabel || 'Keep exploring');
    keepExploring.type = 'button';
    keepExploring.addEventListener('click', () => {
      actions.remove();
      input.focus();
    });
    actions.append(viewCart, keepExploring);
    block.append(actions);
    activePlanBar = null;
    ui.bar.replaceWith(block);
    scrollDown();
  }

  /* ---------- garden visualisation (photo stays in memory only) ---------- */
  const visEndpoint = root.dataset.visEndpoint || '/apps/elli/visualise';
  let scenePhoto = null; // data URL after client-side downscale; never persisted
  let visBusy = false;
  let visFailedOnce = false;

  /* The grounded selection SURVIVES page navigation (root cause of the
     photo-then-dead-end bug: it lived only in memory, so browsing to
     another page wiped it while the conversation text persisted). Only
     public validated product data is stored — never the photo. */
  let lastPlan = []; // most recent validated recommendation set
  let visChosen = {}; // handle -> chosen variant gid for requires_choice pieces
  try {
    const storedVis = JSON.parse(sessionStorage.getItem('elli_vis') || 'null');
    if (storedVis && Array.isArray(storedVis.products)) {
      lastPlan = storedVis.products;
      visChosen = storedVis.chosen && typeof storedVis.chosen === 'object' ? storedVis.chosen : {};
    }
  } catch {
    lastPlan = [];
  }
  const saveVisState = () => {
    try {
      sessionStorage.setItem('elli_vis', JSON.stringify({ products: lastPlan.slice(0, 8), chosen: visChosen }));
    } catch {
      /* storage unavailable — memory state still works this page */
    }
  };
  const setLastPlan = (products) => {
    lastPlan = products;
    const handles = new Set(products.map((p) => p.handle));
    for (const handle of Object.keys(visChosen)) if (!handles.has(handle)) delete visChosen[handle];
    saveVisState();
  };
  const unresolvedVariantItems = () =>
    lastPlan.filter(
      (p) =>
        p.requires_choice === true &&
        Array.isArray(p.variants) &&
        p.variants.length > 1 &&
        !visChosen[p.handle],
    );

  /* Visualisation capability is TRUSTED APPLICATION STATE, not an
     assumption: hidden until the backend (health probe, then every chat
     response) confirms an image provider is actually configured. */
  let visCapable = false;
  let visProbed = false;
  const attachButtonEl = root.querySelector('[data-elli-attach]');
  let launchersAnnounced = false;
  function applyVisCapability(enabled) {
    visCapable = enabled === true;
    if (attachButtonEl) attachButtonEl.style.display = visCapable ? '' : 'none';
    // Contextual launchers elsewhere on the page (the PDP CTA) obey the same
    // trusted state: hidden until the server says a provider exists, hidden
    // again if it ever stops saying so.
    const launchers = document.querySelectorAll('[data-elli-visualise-launcher]');
    for (const node of launchers) node.toggleAttribute('hidden', !visCapable);
    if (visCapable && launchers.length && !launchersAnnounced) {
      launchersAnnounced = true;
      track('elli_visualise_launcher_shown', {
        source: launchers[0].dataset.elliVisualiseLauncher || 'launcher',
      });
    }
    if (!visCapable) {
      root.querySelector('[data-elli-visualise-cta]')?.remove();
      root.querySelector('[data-elli-vis-panel]')?.remove();
    } else {
      // Capability confirmed: surface the next visualisation action for any
      // state restored from the session (e.g. products chosen on a previous
      // page).
      renderVisualiseCta();
    }
  }
  applyVisCapability(false);
  function probeVisCapability() {
    if (visProbed) return;
    visProbed = true;
    fetch(endpoint.replace(/\/chat$/, '/health'))
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => applyVisCapability(data?.visual?.configured === true))
      .catch(() => {}); // stays hidden — fail closed
  }

  // Downscale + re-encode on the device: caps payload size and strips
  // EXIF/GPS metadata before anything leaves the browser.
  async function preparePhoto(file) {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, 1600 / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close?.();
    return canvas.toDataURL('image/jpeg', 0.85);
  }

  function renderPhotoBubble() {
    const bubble = el('div', 'elli-msg elli-msg--user elli-photo-bubble');
    const img = document.createElement('img');
    img.src = scenePhoto;
    img.alt = 'Your space';
    bubble.append(img, el('span', 'elli-photo-bubble__label', root.dataset.photoLabel || 'My space'));
    messagesEl.append(bubble);
    scrollDown();
  }

  /* The visualisation READINESS PANEL: after a photo exists (or products
     exist), there is ALWAYS a visible next action — the old bare CTA
     silently rendered nothing unless every condition held at once, which
     is exactly the dead end customers hit after navigating. Generation
     itself still happens ONLY via the explicit "See it in my space" tap. */
  function renderVisualiseCta() {
    root.querySelector('[data-elli-vis-panel]')?.remove();
    if (!visCapable) return;
    if (!scenePhoto && !lastPlan.length) return;

    const panel = el('div', 'elli-vis-panel');
    panel.setAttribute('data-elli-vis-panel', '');

    if (!scenePhoto) {
      // PRODUCT + NO SCENE → the photo action, spelled out.
      panel.append(
        el('p', 'elli-vis-panel__hint', root.dataset.addPhotoHint || 'Add a photo of your space to see these pieces in place.'),
      );
      const addPhoto = el('button', 'btn elli-vis-panel__secondary', root.dataset.addPhotoLabel || 'Add a photo');
      addPhoto.type = 'button';
      addPhoto.addEventListener('click', () => attachButtonEl?.click());
      panel.append(addPhoto);
    } else if (!lastPlan.length) {
      // SCENE + NO PRODUCT → selection instruction + affordance.
      panel.append(
        el('p', 'elli-vis-panel__hint', root.dataset.choosePieceLabel || 'Choose a piece to place in your space.'),
      );
      const suggest = el('button', 'btn elli-vis-panel__secondary', root.dataset.findPiecesLabel || 'Find pieces');
      suggest.type = 'button';
      suggest.setAttribute('data-elli-vis-suggest', '');
      suggest.addEventListener('click', () =>
        send(root.dataset.findPiecesPrompt || 'Help me choose a piece for my space'),
      );
      panel.append(suggest);
    } else {
      const unresolved = unresolvedVariantItems();
      if (unresolved.length > 0) {
        // VARIANT REQUIRED + NOT CHOSEN → trusted variant selector, no generation.
        for (const product of unresolved) {
          panel.append(
            el('p', 'elli-vis-panel__hint', `${root.dataset.chooseLabel || 'Which one would you like?'} — ${product.title}`),
          );
          const chips = el('div', 'elli-variants');
          for (const variant of product.variants.slice(0, 10)) {
            const chip = el('button', 'elli-variant', variant.title + (variant.available ? '' : ' — sold out'));
            chip.type = 'button';
            if (!variant.available) chip.disabled = true;
            chip.addEventListener('click', () => {
              visChosen[product.handle] = variant.id;
              saveVisState();
              renderVisualiseCta();
            });
            chips.append(chip);
          }
          panel.append(chips);
        }
      } else {
        // SCENE + PRODUCT + VALID VARIANT → the explicit generation action.
        const button = el(
          'button',
          'btn elli-visualise-cta',
          visFailedOnce
            ? root.dataset.tryAgainLabel || 'Try again'
            : root.dataset.visualiseLabel || 'See it in my space',
        );
        button.type = 'button';
        button.setAttribute('data-elli-visualise-cta', '');
        if (visBusy) button.disabled = true;
        button.addEventListener('click', visualise);
        panel.append(button);
      }
    }

    messagesEl.append(panel);
    scrollDown();
  }

  /* ---------- contextual launchers (PDP today, any surface later) ----------
     A launcher is an INTENT SOURCE, never a source of commerce truth. It
     contributes exactly one thing the backend acts on — a product HANDLE,
     which /api/visualise re-resolves and re-validates against live Shopify
     before any provider call. The title/price/image attributes are
     display-only: Shopify rendered them on the page the customer is
     standing on, they are echoed back as a context card, and they are never
     transmitted anywhere.

     The contract is deliberately generic, so a future surface needs markup
     and no new code here:
       <div data-elli-visualise-launcher="<source>" hidden>
         <button data-elli-visualise-product="<handle>"      ← identity
                 data-elli-visualise-variant="<id>"          ← optional hint
                 data-vis-title|price|image="…">             ← display only */
  /* Shopify serves product media from the SHOP's own domain on a custom
     domain (//myelsewhere.com.au/cdn/…) and from cdn.shopify.com otherwise
     — both are the same store asset host. Nothing else may render. */
  const contextImage = (url) => {
    let parsed;
    try {
      parsed = new URL(String(url || ''), location.href);
    } catch {
      return null;
    }
    const ownAsset = parsed.origin === location.origin && parsed.pathname.startsWith('/cdn/');
    const shopifyCdn = parsed.hostname === 'cdn.shopify.com'; // protocol follows the page
    return ownAsset || shopifyCdn ? parsed.href : null;
  };

  /* A variant selected on the product page is a HINT, nothing more. It is
     adopted only if it matches a variant the backend itself returned as
     valid and available; otherwise the customer chooses as they always do. */
  let pendingVariantHint = null;
  let introHandle = null;
  function applyVariantHint(products) {
    const hint = pendingVariantHint;
    if (!hint) return;
    pendingVariantHint = null;
    const product = products.find((p) => p && p.handle === hint.handle);
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const match = variants.find((v) => cartId(v.id) === hint.id && v.available !== false);
    if (!match) return; // no trusted match → nothing is pre-selected
    visChosen[hint.handle] = match.id;
    saveVisState();
  }

  /** Display-only echo of the piece the customer launched from. */
  function renderLaunchContext(handle, data) {
    messagesEl.querySelector('[data-elli-launch-context]')?.remove();
    const wrap = el('div', 'elli-products');
    wrap.setAttribute('data-elli-launch-context', '');
    const card = el('article', 'elli-product');
    const href = productHref(handle); // derived from identity, never supplied
    const main = el(href ? 'a' : 'div', 'elli-product__main');
    if (href) main.href = href;
    const image = contextImage(data.visImage);
    if (image) {
      const img = document.createElement('img');
      img.src = image;
      img.alt = '';
      // Eager: this card is the newest message and always in view, so lazy
      // loading only risks a thumbnail that never arrives.
      img.loading = 'eager';
      main.append(img);
    }
    const info = el('div', 'elli-product__info');
    info.append(el('strong', 'elli-product__title', data.visTitle || ''));
    if (data.visPrice) info.append(el('span', 'elli-product__price', data.visPrice));
    main.append(info);
    card.append(main);
    wrap.append(card);
    messagesEl.append(wrap);
    scrollDown();
  }

  const liveVariantValue = (button) => {
    const selector = button.closest('[data-elli-visualise-launcher]')?.dataset
      .elliVisualiseVariantSource;
    if (!selector) return null;
    try {
      return document.querySelector(selector)?.value ?? null;
    } catch {
      return null; // malformed selector → fall back to the rendered value
    }
  };

  /** Every contextual launcher enters here. Fails closed on identity. */
  function launchProductVisualisation(button) {
    const handle = String(button.dataset.elliVisualiseProduct || '');
    if (!HANDLE_RE.test(handle)) return; // no valid identity → no intent
    const source =
      button.closest('[data-elli-visualise-launcher]')?.dataset.elliVisualiseLauncher || 'launcher';
    /* The rendered variant is whatever Shopify selected when the page was
       built; a launcher may name the live form control instead, so a
       customer who switches variant without reloading is not silently
       given the wrong one. Either way it stays a hint. */
    const variantId = Number(liveVariantValue(button) ?? button.dataset.elliVisualiseVariant);
    pendingVariantHint =
      Number.isSafeInteger(variantId) && variantId > 0 ? { handle, id: variantId } : null;

    openPanel(source);
    setStarted(); // a launcher IS the first turn — onboarding has no role here
    track('elli_visualise_launch', { source, handle });

    /* Identity only. No variant id, price or title enters the plan: the seed
       carries exactly what the request may carry, so nothing read from the
       page can be quoted or added to a cart until the backend has returned
       its own validated product. */
    setLastPlan([{ handle, quantity: 1 }]);
    // The card is always re-rendered (it moves to the foot of the
    // conversation, where the newest thing belongs); only the greeting is
    // said once per piece. A plan restored from an earlier visit must NOT
    // suppress it — the customer would open a panel that never says what
    // they just tapped.
    if (introHandle !== handle) {
      introHandle = handle;
      renderBubble('assistant', root.dataset.launchIntro || "Let's see this piece in your garden.");
    }
    renderLaunchContext(handle, button.dataset);
    renderVisualiseCta(); // the existing readiness panel owns what happens next
  }

  async function addLookToCart(products, button) {
    const items = products.map((p) => ({
      id: cartId(p.variant_id),
      quantity: p.quantity || 1,
      properties: { _elli: '1', _elli_vis: '1' },
    }));
    if (items.some((line) => !line.id)) return; // untrusted/unresolved → never mutate
    button.disabled = true;
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error(String(res.status));
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { elli_assisted: 'true', visualisation_assisted: 'true' } }),
      });
      sessionStorage.setItem('elli_cart_marked', '1');
      track('elli_preview_add_to_cart', { items: products.length });
      track('add_to_cart', { source: 'elli-vis', items: products.length });
      button.textContent = root.dataset.addedLabel || 'Added ✓';
      window.MECart?.refresh(true);
    } catch {
      button.disabled = false;
    }
  }

  async function visualise() {
    // Explicit consent only, and never with an unresolved variant choice.
    if (visBusy || !scenePhoto || !lastPlan.length || unresolvedVariantItems().length > 0) return;
    visBusy = true;
    root.querySelector('[data-elli-vis-panel]')?.remove();
    track('elli_visual_preview_requested', { products: lastPlan.length });
    const pending = renderBubble('assistant', root.dataset.visPendingLabel || 'Creating your preview…');
    pending.classList.add('elli-msg--pending');

    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 65_000);
      const res = await fetch(visEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          image: scenePhoto,
          products: lastPlan.slice(0, 5).map((p) => ({ handle: p.handle, quantity: p.quantity || 1 })),
        }),
        signal: controller.signal,
      });
      clearTimeout(timer);
      const data = await res.json().catch(() => null);

      pending.remove();
      if (data?.ok && data.image) {
        track('elli_visual_preview_completed', {});
        const figure = el('figure', 'elli-vis-preview');
        const img = document.createElement('img');
        img.src = data.image;
        img.alt = 'AI styling preview of the look in your space';
        const caption = el(
          'figcaption',
          'elli-vis-preview__disclaimer',
          data.disclaimer || 'AI visualisation — actual scale, colour and placement may vary.',
        );
        figure.append(img, caption);
        messagesEl.append(figure);
        visFailedOnce = false;
        const products = Array.isArray(data.products) ? data.products : [];
        if (products.length) {
          setLastPlan(products);
          applyVariantHint(products);
          const planItems = buildPlanItems(products);
          renderProducts(planItems, { clickEvent: 'elli_preview_product_click' });
          if (planItems.some((item) => item.needsChoice)) {
            // A variant still needs choosing — consent goes through the plan
            // bar, which requires the choice before anything can be added.
            renderPlanBar(planItems);
          } else {
            const addLook = el('button', 'btn elli-vis-add', root.dataset.addLookLabel || 'Add this look to cart');
            addLook.type = 'button';
            addLook.addEventListener('click', () => addLookToCart(products, addLook));
            messagesEl.append(addLook);
          }
        }
        scrollDown();
      } else {
        // Enhancement failure: the grounded selection survives (spec §26).
        track('elli_visual_preview_failed', {});
        renderBubble(
          'assistant',
          data?.message || "I couldn't create the preview this time, but your garden selection is still ready.",
        );
        visFailedOnce = true;
        const products = Array.isArray(data?.products) ? data.products : lastPlan;
        if (products.length) {
          setLastPlan(products);
          applyVariantHint(products);
          renderProducts(buildPlanItems(products), { clickEvent: 'elli_preview_product_click' });
        }
      }
    } catch {
      pending.remove();
      visFailedOnce = true;
      track('elli_visual_preview_failed', {});
      renderBubble(
        'assistant',
        "I couldn't create the preview this time, but your garden selection is still ready.",
      );
    } finally {
      visBusy = false;
      // The next action is always visible: "See it in my space" again on
      // success, "Try again" after any failure.
      renderVisualiseCta();
    }
  }

  /* ---------- human handoff ("Leave a word") ----------
     The destination lives only in server configuration; nothing here knows
     or can influence it. Offered only when the server says so — never
     decided in the browser. */
  const supportEndpoint = root.dataset.supportEndpoint || '/apps/elli/support';
  const supportDialog = root.querySelector('[data-elli-support]');
  const supportAsk = root.querySelector('[data-elli-support-ask]');
  const supportForm = root.querySelector('[data-elli-support-form]');
  const supportDone = root.querySelector('[data-elli-support-done]');
  const supportError = root.querySelector('[data-elli-support-error]');
  const supportSend = root.querySelector('[data-elli-support-send]');
  let supportBusy = false;

  const showSupportError = (text) => {
    if (!supportError) return;
    supportError.textContent = text;
    supportError.hidden = false; // role="alert" announces it
  };

  /** Back to the asking view — used when opening, never after a success. */
  function resetSupportView() {
    supportAsk?.removeAttribute('hidden');
    supportForm?.removeAttribute('hidden');
    supportDone?.setAttribute('hidden', '');
    if (supportError) {
      supportError.hidden = true;
      supportError.textContent = '';
    }
    if (supportSend) {
      supportSend.disabled = false;
      supportSend.textContent = root.dataset.supportSendLabel || 'Send';
    }
    supportDialog?.setAttribute('aria-labelledby', 'elli-support-heading');
  }

  function openSupport() {
    if (!supportDialog) return;
    resetSupportView();
    supportDialog.showModal(); // modal: focus containment + Escape for free
    supportDialog.querySelector('input[name="email"]')?.focus();
    track('elli_support_opened', {});
  }

  const closeSupport = () => supportDialog?.close();
  root.querySelectorAll('[data-elli-support-close], [data-elli-support-cancel]').forEach((el) =>
    el.addEventListener('click', closeSupport),
  );

  function renderHandoffCta() {
    if (!supportDialog) return;
    messagesEl.querySelector('[data-elli-support-cta]')?.remove();
    const button = el('button', 'btn elli-support-cta', root.dataset.supportCtaLabel || 'Leave a word');
    button.type = 'button';
    button.setAttribute('data-elli-support-cta', '');
    button.addEventListener('click', openSupport);
    messagesEl.append(button);
    scrollDown();
  }

  supportForm?.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (supportBusy) return;
    const data = new FormData(supportForm);
    const payload = {
      session_id: sessionId,
      email: String(data.get('email') || '').trim(),
      message: String(data.get('message') || '').trim(),
    };
    const orderNumber = String(data.get('order_number') || '').trim();
    if (orderNumber) payload.order_number = orderNumber;

    // Client-side checks are courtesy only — the server validates again.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.email)) {
      showSupportError('Please enter a valid email address.');
      return;
    }
    if (payload.message.length < 10) {
      showSupportError('Please add a little more detail.');
      return;
    }

    // Pending state: the button says so, and both the flag and the disabled
    // attribute block a second submit (click OR Enter) while in flight.
    supportBusy = true;
    if (supportError) supportError.hidden = true;
    if (supportSend) {
      supportSend.disabled = true;
      supportSend.textContent = root.dataset.supportSendingLabel || 'Sending…';
    }
    let succeeded = false;
    try {
      const res = await fetch(supportEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => null);
      if (res.ok && body?.ok) {
        succeeded = true;
        // Analytics never carries the email, order number or message.
        track('elli_support_sent', {});
        // Replace the whole asking view: nothing left to submit twice.
        supportAsk?.setAttribute('hidden', '');
        supportForm.setAttribute('hidden', '');
        supportForm.reset();
        supportDone?.removeAttribute('hidden');
        supportDialog?.setAttribute('aria-labelledby', 'elli-support-done-heading');
        supportDone?.focus(); // announced via role="status"
      } else {
        showSupportError(body?.message || root.dataset.supportErrorLabel || "I couldn't send that just now.");
      }
    } catch {
      showSupportError(root.dataset.supportErrorLabel || "I couldn't send that just now.");
    } finally {
      supportBusy = false;
      // On failure the customer's text is untouched and SEND comes back.
      if (supportSend && !succeeded) {
        supportSend.disabled = false;
        supportSend.textContent = root.dataset.supportSendLabel || 'Send';
      }
    }
  });

  /* ---------- conversation ---------- */
  let busy = false;
  async function send(text) {
    const content = text.trim().slice(0, 1000);
    if (!content || busy) return;
    busy = true;
    setStarted();
    input.value = '';
    renderBubble('user', content);
    history.push({ role: 'user', content });
    saveHistory();
    track('elli_message', { length: content.length });

    const thinking = renderBubble('assistant', root.dataset.thinkingLabel || 'Elli is thinking…');
    thinking.classList.add('elli-msg--pending');

    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 30_000);
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, messages: history }),
        signal: controller.signal,
      });
      clearTimeout(timer);
      const data = res.ok ? await res.json() : null;
      const reply = data?.reply || root.dataset.errorLabel || 'Elli is unavailable right now. Please try again shortly.';
      thinking.remove();
      renderBubble('assistant', reply);
      history.push({ role: 'assistant', content: reply.slice(0, 1000) });
      saveHistory();
      if (data?.capabilities) {
        applyVisCapability(data.capabilities.visualisation?.enabled === true);
      }
      // Server-decided: a person should take this one.
      if (data?.human_handoff) renderHandoffCta();
      // Honest partial-match note (server-clamped trusted data): tells the
      // customer which requested requirements couldn't be verified.
      if (
        data?.match_status === 'partial' &&
        Array.isArray(data.unmet_constraints) &&
        data.unmet_constraints.length
      ) {
        const items = data.unmet_constraints.slice(0, 5).map((c) => String(c).slice(0, 60));
        const note = el(
          'p',
          'elli-unverified',
          `${root.dataset.unverifiedLabel || "I couldn't verify"}: ${items.join(' · ')} — ${
            root.dataset.unverifiedHint || 'full details are on each product page.'
          }`,
        );
        messagesEl.append(note);
      }
      const products = Array.isArray(data?.products) ? data.products : [];
      if (products.length) {
        track('elli_recommendation', { count: products.length });
        const planItems = buildPlanItems(products);
        renderProducts(planItems);
        renderPlanBar(planItems);
        setLastPlan(products);
        if (scenePhoto) track('elli_space_analysis', { count: products.length });
      }
      renderVisualiseCta();
    } catch {
      thinking.remove();
      renderBubble(
        'assistant',
        root.dataset.errorLabel || 'Elli is unavailable right now. Please try again shortly.',
      );
    } finally {
      busy = false;
    }
  }

  /* ---------- wiring ---------- */
  const openPanel = (source) => {
    dialog.show(); // non-modal: the shop stays browsable behind the panel
    probeVisCapability();
    track('elli_open', source ? { source } : undefined);
    if (messagesEl.childElementCount === 0) {
      for (const turn of history) renderBubble(turn.role, turn.content);
      scrollDown();
    }
    input.focus();
  };
  launcher.addEventListener('click', () => openPanel());
  // Any CTA elsewhere on the page can open the panel (Design My Garden,
  // PDP "Ask Elli", homepage invite) — delegated so sections stay dumb.
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-elli-open]');
    if (!opener) return;
    event.preventDefault();
    openPanel(opener.dataset.elliOpen || 'cta');
  });
  // Contextual visualisation launchers, delegated like every other opener.
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-elli-visualise-product]');
    if (!button) return;
    event.preventDefault();
    launchProductVisualisation(button);
  });
  // A launcher must be able to appear without the panel ever being opened,
  // so the capability probe runs on load wherever one exists.
  if (document.querySelector('[data-elli-visualise-launcher]')) probeVisCapability();

  root.querySelector('[data-elli-close]')?.addEventListener('click', () => dialog.close());

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    send(input.value);
  });
  quickActions.forEach((chip) =>
    chip.addEventListener('click', () => send(chip.dataset.elliQuick || chip.textContent || '')),
  );

  // Photo attach: file → downscale on device → memory only.
  const attachButton = root.querySelector('[data-elli-attach]');
  const fileInput = root.querySelector('[data-elli-file]');
  const disclosure = root.querySelector('[data-elli-disclosure]');
  if (attachButton && fileInput) {
    attachButton.addEventListener('click', () => {
      // Pre-upload privacy disclosure, shown contextually the first time the
      // picker opens — before any photo can be chosen or sent.
      if (disclosure && !disclosure.dataset.shown) {
        disclosure.dataset.shown = '1';
        disclosure.hidden = false;
        messagesEl.append(disclosure);
        scrollDown();
      }
      fileInput.click();
    });
    fileInput.addEventListener('change', async () => {
      const file = fileInput.files?.[0];
      fileInput.value = '';
      if (!file || !file.type.startsWith('image/')) return;
      try {
        scenePhoto = await preparePhoto(file);
        track('elli_photo_upload', {}); // event only — never the image
        renderPhotoBubble();
        renderVisualiseCta();
      } catch {
        renderBubble('assistant', root.dataset.photoErrorLabel || "I couldn't read that photo — try a JPG or PNG.");
      }
    });
  }
})();
